import React, {
  useEffect, useMemo, useState,
} from 'react';

import {
  Grid, Stack, Typography,
} from '@mui/material';

import Chip from '../../../../components/Chip';
import ContentCard from '../../../../components/ContentCard';
import LessonEnrolmentCard from '../../../../components/LessonEnrolmentCard';
import SkeletonStack from '../../../../components/SkeletonStack';
import { useGetStudentLessonEnrolmentsQuery } from '../../../../services/classes';
import { LessonEnrolment } from '../../../../services/classes/types';
import { useGetCoursesQuery } from '../../../../services/courses';
import { Student } from '../../../../services/students/types';
import { BillingType } from '../../../../types/common';
import { getLessonEnrolmentCourse, getLessonEnrolmentSubject } from '../../../../utils/common/classes';
import { SUBJECTS } from '../../../../utils/common/constants';

import ClassesCard from './ClassesCard';
import WaitlistCard from './WaitlistCard';

interface ClassesTabProps {
  student?: Student,
  selectedLesson?: LessonEnrolment,
  onSelectLesson: (lesson: LessonEnrolment) => void,
}

interface BillingCounter {
  [key: string]: {
    [BillingType.Term]: number,
    [BillingType.Extra]: number,
  }
}

interface InvoicedLessonEnrolment extends LessonEnrolment {
  isInvoiced: boolean
}

const ClassesTab = ({
  student,
  selectedLesson,
  onSelectLesson,
}: ClassesTabProps) => {
  const { data: lessonEnrolments } = useGetStudentLessonEnrolmentsQuery({
    studentId: student?.id ?? '',
  }, { skip: !student });
  const { data: courses } = useGetCoursesQuery({});
  const [showMarkedLessons, setShowMarkedLessons] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (lessonEnrolments?.data) {
      // Extract subjects from lessonEnrolments
      const subjects = lessonEnrolments.data.map((l) => getLessonEnrolmentSubject(l)) || [];
      const uniqueSubjects = [...new Set(subjects)];

      const newSubjectsSelected = uniqueSubjects.reduce((acc, subject) => {
        acc[subject] = selectedSubjects[subject] ?? true;
        return acc;
      }, {} as { [key: string]: boolean });

      setSelectedSubjects(newSubjectsSelected);
    }
  }, [lessonEnrolments]);

  // Map lesson enrolments to include invoiced status
  const mappedEnrolments = useMemo(() => {
    if (!student || !lessonEnrolments) return undefined;

    const courseStates = Object.values(student.courseStates);

    // Tally of the number of term and extra lessons invoiced for each course
    const billingCount: BillingCounter = courseStates.reduce((acc, courseState) => {
      const extraLessonsCompleted = lessonEnrolments.data
        .filter((lesson) => lesson.billingType === BillingType.Extra
      && getLessonEnrolmentCourse(lesson) === courseState.course
      && lesson.rollMarked)
        .length;

      // Number of lessons that's been invoiced is the number of lessons already finished
      // plus the number of outstanding credits for the course
      acc[courseState.course] = {
        [BillingType.Term]: courseState.termCredits + (
          courseState.lessonsCompleted - extraLessonsCompleted),
        [BillingType.Extra]: courseState.extraCredits + extraLessonsCompleted,
      };
      return acc;
    }, {} as BillingCounter);

    return lessonEnrolments.data.map((lesson) => {
      const course = getLessonEnrolmentCourse(lesson);
      const billingType = lesson.billingType as BillingType;
      if (billingCount?.[course]?.[billingType] >= 0) {
        billingCount[course][billingType]--;
      }

      return {
        ...lesson,
        isExtra: lesson.billingType === BillingType.Extra,
        // Treat any lessons which aren't recorded on courseStates as invoiced
        // Note: Every lesson should be included in courseStates, but this is a safety
        // to prevent page crashes
        isInvoiced: billingCount?.[course]?.[billingType] >= 0,
      };
    });
  }, [student, lessonEnrolments]);

  const filteredLessonEnrolments = useMemo(() => mappedEnrolments?.filter(
    (lessonEnrolment: InvoicedLessonEnrolment) => {
      const subject = getLessonEnrolmentSubject(lessonEnrolment);
      const subjectSelected = selectedSubjects[subject];
      return subjectSelected && (showMarkedLessons || !lessonEnrolment.rollMarked);
    },
  ), [lessonEnrolments, showMarkedLessons, selectedSubjects]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ClassesCard classes={student?.classes || []} />
      </Grid>
      {student && student.waitlistedClasses.length > 0 && (
        <Grid item xs={12}>
          <WaitlistCard student={student} />
        </Grid>
      )}
      <Grid item xs={12}>
        <ContentCard variant="outlined" title="Lessons">
          <Stack direction="row" spacing={1} marginBottom={2}>
            <Chip
              label="Upcoming Only"
              onClick={() => setShowMarkedLessons((prev) => !prev)}
              variant={showMarkedLessons ? 'outlined' : 'filled'}
              color="gray"
              cursorType="pointer"
            />
            {Object.keys(selectedSubjects).length > 1 && (
              Object.keys(SUBJECTS)
                .filter((key) => selectedSubjects[SUBJECTS[key].value] !== undefined)
                .map((subject) => (
                  <Chip
                    key={subject}
                    label={SUBJECTS[subject].label}
                    onClick={() => setSelectedSubjects((prev) => ({
                      ...prev,
                      [subject]: !prev[subject],
                    }))}
                    variant={selectedSubjects[subject] ? 'filled' : 'outlined'}
                    color={SUBJECTS[subject].colorKey}
                    cursorType="pointer"
                  />
                ))
            )}
          </Stack>
          <Stack direction="column" spacing={1} marginTop={2}>
            {courses ? (
              <>
                {lessonEnrolments?.data.length === 0 && (
                  <Typography variant="overline">
                    No Lessons Found
                  </Typography>
                )}
                {filteredLessonEnrolments?.map((lessonEnrolment) => (
                  <LessonEnrolmentCard
                    isInvoiced={lessonEnrolment.isInvoiced}
                    isExtra={lessonEnrolment.isExtra}
                    key={lessonEnrolment.id}
                    courses={courses.data ?? []}
                    lessonEnrolment={lessonEnrolment}
                    onSelect={() => onSelectLesson(lessonEnrolment)}
                    selected={selectedLesson?.id === lessonEnrolment.id}
                    greyedOut={lessonEnrolment.rollMarked}
                  />
                ))}
              </>
            ) : <SkeletonStack />}
          </Stack>
        </ContentCard>
      </Grid>
    </Grid>
  );
};

export default ClassesTab;
